import { api, type InitUploadSessionRequest, type UploadSessionStatusData } from "@/lib/api"

/* Sessions being actively driven by this tab right now. GET /uploads/sessions/paused reports any
   in_progress session — including one this same tab is mid-upload on — so callers that surface
   "resume/discard" UI (e.g. PausedUploadsSection) must exclude these to avoid a user discarding
   (and thereby aborting) their own live upload out from under it. */
export const activeUploadSessionIds = new Set<string>()

export type UploadedPartRecord = { part_number: number; etag: string; size: number }

/** The backend has returned uploadSessionId in some responses and session_id in others — read whichever is present. */
export function uploadSessionIdOf(s: { session_id?: string; uploadSessionId?: string }): string {
  return s.uploadSessionId ?? s.session_id ?? ""
}

/** uploaded_parts may come back as bare part numbers or as {part_number, etag, size} records. */
export function normalizeUploadedParts(
  raw: UploadSessionStatusData["uploaded_parts"],
): { part_number: number; etag?: string; size?: number }[] {
  return raw.map((p) => (typeof p === "number" ? { part_number: p } : p))
}

function expectedPartSize(partNumber: number, partSize: number, totalParts: number, fileSize: number): number {
  return partNumber < totalParts ? partSize : fileSize - partSize * (totalParts - 1)
}

// S3 multipart upload limits (https://docs.aws.amazon.com/AmazonS3/latest/userguide/qfacts.html):
// parts must be 5 MiB–5 GiB (no minimum on the last part), and a single upload can have at most
// 10,000 parts. DEFAULT_PART_SIZE matches what this app has been using for ordinary files; PART_CAP
// is kept below the hard 10,000 limit for headroom, so the size only ever scales up from the default
// once a file is big enough that 10 MiB chunks would otherwise blow through that many parts.
const S3_MIN_PART_SIZE = 5 * 1024 * 1024
const S3_MAX_PART_SIZE = 5 * 1024 * 1024 * 1024
const DEFAULT_PART_SIZE = 10 * 1024 * 1024
const PART_COUNT_CAP = 8000

/** The one place that decides how a file gets chunked. Sent explicitly in both the session-create and
 *  resume requests (see initUploadSession / resolveResumeInfoByFingerprint below) and used directly
 *  for slicing — the backend is never asked to guess or re-derive this, and since it's a pure function
 *  of file size alone, a resume can never land on a different value than the original upload did. */
export function computePartSize(fileSize: number): number {
  const minToStayUnderPartsCap = Math.ceil(fileSize / PART_COUNT_CAP)
  return Math.min(S3_MAX_PART_SIZE, Math.max(DEFAULT_PART_SIZE, minToStayUnderPartsCap, S3_MIN_PART_SIZE))
}

// 512 miB chunks are a good balance between hashing speed and memory usage — large enough to avoid the overhead of too many postMessage() calls, but small enough to avoid freezing the UI on large files.
const HASH_READ_CHUNK_SIZE = 512 * 1024 * 1024

type HashOutMessage =
  | { id: number; type: "progress"; bytesProcessed: number }
  | { id: number; type: "done"; hash: string }
  | { id: number; type: "error"; message: string }

let sha256Worker: Worker | null = null
let nextHashJobId = 0
const pendingHashJobs = new Map<number, {
  onProgress?: (bytesProcessed: number) => void
  resolve: (hash: string) => void
  reject: (err: Error) => void
}>()

function getSha256Worker(): Worker {
  if (!sha256Worker) {
    sha256Worker = new Worker(new URL("./sha256-worker.ts", import.meta.url), { type: "module" })
    sha256Worker.onmessage = (e: MessageEvent<HashOutMessage>) => {
      const msg = e.data
      const pending = pendingHashJobs.get(msg.id)
      if (!pending) return
      if (msg.type === "progress") {
        pending.onProgress?.(msg.bytesProcessed)
      } else if (msg.type === "done") {
        pendingHashJobs.delete(msg.id)
        pending.resolve(msg.hash)
      } else {
        pendingHashJobs.delete(msg.id)
        pending.reject(new Error(msg.message))
      }
    }
  }
  return sha256Worker
}

/** SHA-256 of the full file contents, hex-encoded — sent as file_hash when initiating a session, and
    used again to verify a re-selected file against its stored hash before resuming a paused upload.
    Hashed off the main thread (see lib/sha256-worker.ts) so nothing here can ever freeze the UI,
    regardless of file size or chunk size. */
export function computeFileSha256(file: File, onProgress?: (bytesProcessed: number) => void): Promise<string> {
  const worker = getSha256Worker()
  const id = nextHashJobId++
  return new Promise<string>((resolve, reject) => {
    pendingHashJobs.set(id, { onProgress, resolve, reject })
    worker.postMessage({ id, file, chunkSize: HASH_READ_CHUNK_SIZE })
  })
}

export type PendingUploadSessionRecord = {
  upload_session_id: string
  file_id: string
  project_id: string
  parent_id?: string
  source_path: string
  file_hash: string
}

/* Sessions that have been initiated (POST /uploads/sessions succeeded) but not yet completed.
   Kept in IndexedDB — rather than just `activeUploadSessionIds` — so the record survives a
   page reload/crash mid-upload. */
const UPLOAD_SESSIONS_DB_NAME = "rudrise-uploads"
const UPLOAD_SESSIONS_DB_VERSION = 1
const PENDING_UPLOAD_SESSIONS_STORE = "pending-upload-sessions"

function openUploadSessionsDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"))
      return
    }
    const req = indexedDB.open(UPLOAD_SESSIONS_DB_NAME, UPLOAD_SESSIONS_DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(PENDING_UPLOAD_SESSIONS_STORE)) {
        db.createObjectStore(PENDING_UPLOAD_SESSIONS_STORE, { keyPath: "upload_session_id" })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function runUploadSessionsTx(mode: IDBTransactionMode, run: (store: IDBObjectStore) => void): Promise<void> {
  return openUploadSessionsDb()
    .then(
      (db) =>
        new Promise<void>((resolve, reject) => {
          const tx = db.transaction(PENDING_UPLOAD_SESSIONS_STORE, mode)
          run(tx.objectStore(PENDING_UPLOAD_SESSIONS_STORE))
          tx.oncomplete = () => {
            db.close()
            resolve()
          }
          tx.onerror = () => {
            db.close()
            reject(tx.error)
          }
        }),
    )
    .catch(() => {
      // ignore — best-effort persistence, never block the upload itself
    })
}

/** Single transaction covering every session in a batch, so a bulk file/folder selection lands as one IndexedDB write. */
function savePendingUploadSessions(records: PendingUploadSessionRecord[]): Promise<void> {
  if (records.length === 0) return Promise.resolve()
  return runUploadSessionsTx("readwrite", (store) => {
    for (const record of records) store.put(record)
  })
}

/** Also used to clean up IndexedDB when the user discards/aborts a paused session outright. */
export function clearPendingUploadSession(uploadSessionId: string): Promise<void> {
  return runUploadSessionsTx("readwrite", (store) => {
    store.delete(uploadSessionId)
  })
}

/** Every session this browser has initiated but not yet completed — read to cross-check against the
    paused-list API and, crucially, to recover each file's original file_hash for local tamper checks. */
export function getAllPendingUploadSessions(): Promise<PendingUploadSessionRecord[]> {
  return openUploadSessionsDb()
    .then(
      (db) =>
        new Promise<PendingUploadSessionRecord[]>((resolve, reject) => {
          const tx = db.transaction(PENDING_UPLOAD_SESSIONS_STORE, "readonly")
          const req = tx.objectStore(PENDING_UPLOAD_SESSIONS_STORE).getAll()
          req.onsuccess = () => {
            db.close()
            resolve(req.result as PendingUploadSessionRecord[])
          }
          req.onerror = () => {
            db.close()
            reject(req.error)
          }
        }),
    )
    .catch(() => [])
}

/* Upload one part's raw bytes via the Next.js proxy, returning the S3 ETag.
   The upload_url comes from POST /uploads/sessions/parts/url — not constructed here.
   Browser → /api/upload/proxy (PUT, raw body) → proxy PUTs to upload_url server-side.
   Sent as a raw body (not multipart FormData) since large parts can fail multipart parsing. */
export function uploadPartToS3(
  uploadUrl: string,
  blob: Blob,
  mimeType: string,
  onProgress: (pct: number) => void,
  xhrRef: React.RefObject<XMLHttpRequest | null>,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhrRef.current = xhr

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    })
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100)
        try {
          const body = JSON.parse(xhr.responseText || "{}") as { etag?: string | null }
          resolve(body.etag ?? "")
        } catch {
          resolve("")
        }
      } else {
        reject(new Error(`Upload failed (HTTP ${xhr.status})`))
      }
    })
    xhr.addEventListener("error", () => reject(new Error("Network error")))
    xhr.addEventListener("abort", () => reject(new Error("Cancelled")))

    xhr.open("PUT", "/api/upload/proxy")
    xhr.setRequestHeader("x-upload-url", uploadUrl)
    xhr.setRequestHeader("x-mime-type", mimeType || "application/octet-stream")
    xhr.send(blob)
  })
}

export type FreshSessionInit = {
  kind: "fresh"
  uploadSessionId: string
  part_size: number
  total_parts: number
  file_id: string
  project_id: string
  parent_id?: string
  source_path: string
  file_hash: string
}

export type ResumeSessionInfo = {
  kind: "resume"
  uploadSessionId: string
  part_size: number
  total_parts: number
  /** Parts already confirmed uploaded (with ETags) — these are skipped and merged into the final complete() call. */
  uploadedParts: UploadedPartRecord[]
  /** Straight from the resume/status API's own `uploaded_bytes` — used to seed the resumed upload's
   * displayed progress by actual bytes rather than by part count (see uploadFileWithSession), since
   * part-count math is only as good as part_size/total_parts, which can drift (see inferPartSize). */
  uploadedBytes: number
  /** The backend's own authoritative continuation point, when it sends one — 0 means every part it
   * expects is already confirmed; uploadFileWithSession trusts this over re-deriving "how many parts
   * are left" from part_size math, which can over-extend when part_size has drifted (see
   * inferPartSize). Undefined on older responses that don't send this field at all — falls back to
   * the part_size-derived heuristic in that case, same as before this field existed. */
  nextPartNumber?: number
}

/** Either a freshly-initiated session or a resolved resume point — whichever `uploadFileWithSession` drives. */
export type UploadSessionHandle = FreshSessionInit | ResumeSessionInfo

/* Initiates one session (POST /uploads/sessions) — hashing the file and calling the backend, but not
   touching localStorage. Callers that are bulk-starting several uploads should call this for every file
   first, then persist them all via savePendingUploadSessionBatch() in a single write before uploading any bytes. */
export async function initUploadSession(
  file: File,
  opts: { project_id?: string; parent_id?: string },
): Promise<FreshSessionInit> {
  const file_hash = await computeFileSha256(file)
  const part_size = computePartSize(file.size)
  const initResp = await api.initUploadSession({
    file_name: file.name,
    file_size: file.size,
    mime_type: file.type || "application/octet-stream",
    file_hash,
    part_size,
    ...(opts.project_id && { project_id: opts.project_id }),
    ...(opts.parent_id && { parent_id: opts.parent_id }),
  })
  return {
    kind: "fresh",
    uploadSessionId: initResp.data.uploadSessionId,
    // Ours, not initResp.data.part_size/total_parts — we told the backend exactly what we're using,
    // so there's nothing to re-derive from its response here.
    part_size,
    total_parts: Math.ceil(file.size / part_size),
    file_id: initResp.data.file_id,
    project_id: initResp.data.project_id,
    ...(opts.parent_id && { parent_id: opts.parent_id }),
    source_path: file.webkitRelativePath || file.name,
    file_hash,
  }
}

/** Bulk-persist every freshly-initiated session from a batch in one IndexedDB write. */
export function savePendingUploadSessionBatch(sessions: FreshSessionInit[]): Promise<void> {
  return savePendingUploadSessions(
    sessions.map((s) => ({
      upload_session_id: s.uploadSessionId,
      file_id: s.file_id,
      project_id: s.project_id,
      ...(s.parent_id && { parent_id: s.parent_id }),
      source_path: s.source_path,
      file_hash: s.file_hash,
    })),
  )
}

/* Upload a single file's bytes through an already-initiated (or resumed) session:
   PUT each missing part to its signed URL, then complete the session. */
export async function uploadFileWithSession(
  file: File,
  session: UploadSessionHandle,
  onFileProgress: (pct: number) => void,
  waitWhilePaused: () => Promise<boolean>,
  xhrRef: React.RefObject<XMLHttpRequest | null>,
  onSessionStart: (uploadSessionId: string) => void,
): Promise<void> {
  const mimeType = file.type || "application/octet-stream"
  const { uploadSessionId, part_size, total_parts } = session
  const parts: UploadedPartRecord[] = session.kind === "resume" ? [...session.uploadedParts] : []
  // Byte-accurate running total, seeded from the resume API's own `uploaded_bytes` when resuming —
  // NOT reconstructed from partNumber/safeTotalParts below, which is only as good as part_size and
  // total_parts staying in sync with each other. When they drift (see inferPartSize), part-count math
  // can under- or overshoot badly (e.g. showing a resume starting at 29% instead of the true ~82%
  // for 9/11 parts already done, because total_parts got inflated against a stale part_size). Bytes
  // don't have that failure mode: file.size and uploaded_bytes are both reported directly, independent
  // of how the file happens to be chunked.
  let uploadedBytesTotal = session.kind === "resume" ? session.uploadedBytes : 0

  onSessionStart(uploadSessionId)
  activeUploadSessionIds.add(uploadSessionId)

  try {
    const alreadyDone = new Set(parts.map((p) => p.part_number))

    // The backend's own next_part_number, when it sends one, is authoritative — 0 means every part
    // it expects is already confirmed, and nothing else should be requested (asking for one more, as
    // the part_size-derived math below can do when part_size has drifted, gets rejected outright:
    // "invalid upload part number"). Only fall back to re-deriving a stopping point from part sizes
    // when the backend doesn't send this field at all (older responses).
    const nothingLeftToUpload = session.kind === "resume" && session.nextPartNumber === 0

    if (!nothingLeftToUpload) {
      // total_parts is always Math.ceil(file.size / part_size) by construction now (see
      // initUploadSession / toResumeSessionInfo — both compute it from part_size themselves rather
      // than trusting a separately-reported backend value), so there's no drift left to guard
      // against here; `start >= file.size` below is still what actually stops the loop.
      for (let partNumber = 1; partNumber <= total_parts; partNumber++) {
        if (alreadyDone.has(partNumber)) continue
        if ((partNumber - 1) * part_size >= file.size) break

        /* Pausing (or a tab close/refresh) forcefully aborts whatever part is in flight — rather than
           letting it silently finish uploading in the background. That abort surfaces here as a
           "Cancelled" rejection; loop back to wait-while-paused and retry the same part once resumed.
           If this was actually a hard Stop, waitWhilePaused() sees cancelRef and returns true, and we
           return cleanly below without ever re-hitting the network — no need to call abort-session for
           either case, since the session itself is left untouched and still resumable. */
        for (;;) {
          if (await waitWhilePaused()) return

          const start = (partNumber - 1) * part_size
          const end   = Math.min(start + part_size, file.size)
          const blob  = file.slice(start, end)

          const urlResp = await api.getUploadPartUrl({ uploadSessionId, part_number: partNumber })

          try {
            const etag = await uploadPartToS3(
              urlResp.data.upload_url,
              blob,
              mimeType,
              (pct) => onFileProgress(Math.min(100, Math.round(((uploadedBytesTotal + (blob.size * pct) / 100) / file.size) * 100))),
              xhrRef,
            )
            parts.push({ part_number: partNumber, etag, size: blob.size })
            uploadedBytesTotal += blob.size
            break
          } catch (err) {
            if (!(err instanceof Error && err.message === "Cancelled")) throw err
          }
        }
      }
    }

    parts.sort((a, b) => a.part_number - b.part_number)
    await api.completeUploadSession({ uploadSessionId, parts })
    await clearPendingUploadSession(uploadSessionId)
  } finally {
    activeUploadSessionIds.delete(uploadSessionId)
  }
}

/* The session's reported part_size can drift from what was actually used to chunk parts that are
   already confirmed on the server — e.g. if the backend's default part_size changed sometime after
   this session was created, the resume endpoint may report its current default instead of the
   value this session was actually fixed to at creation time. S3 doesn't require uniform part sizes,
   so when the already-uploaded parts consistently agree on some other size, that's almost certainly
   the session's real chunking — trust it over the fallback, which would otherwise misalign every
   confirmed part's byte range and force the whole file to restart from part 1. */
function inferPartSize(uploadedParts: { size?: number }[], fallbackPartSize: number): number {
  const counts = new Map<number, number>()
  for (const p of uploadedParts) {
    if (p.size != null) counts.set(p.size, (counts.get(p.size) ?? 0) + 1)
  }
  let bestSize = fallbackPartSize
  let bestCount = 0
  for (const [size, count] of counts) {
    if (count > bestCount) {
      bestCount = count
      bestSize = size
    }
  }
  // Require at least two parts agreeing before overriding — a single part is too little evidence
  // to distinguish "the real chunk size" from an ordinary truncated/incomplete part.
  return bestCount >= 2 ? bestSize : fallbackPartSize
}

function toResumeSessionInfo(data: UploadSessionStatusData): ResumeSessionInfo {
  const { file_size } = data
  const normalized = normalizeUploadedParts(data.uploaded_parts)
  const withEtag = normalized.filter((p): p is { part_number: number; etag: string; size?: number } => !!p.etag)

  // Fall back to our own deterministic part_size — the same one sent as `part_size` in the resume
  // request itself (see resolveResumeInfoByFingerprint) — rather than the backend's echoed
  // data.part_size, which is what drifted in the first place on older/legacy sessions. Already-
  // confirmed parts are still the ground truth when they disagree with that (a session created
  // before this change, chunked at whatever the backend picked back then).
  const part_size = inferPartSize(withEtag, computePartSize(file_size))
  // Recomputed from file_size/part_size instead of trusting data.total_parts directly — total_parts
  // is now always internally consistent with whichever part_size we actually settled on above,
  // rather than potentially the backend's separate (and possibly stale) accounting. Knowing when to
  // stop asking for more parts is handled by next_part_number below, not by capping at this number.
  const total_parts = Math.ceil(file_size / part_size)

  const uploadedParts: UploadedPartRecord[] = withEtag
    .map((p) => ({
      part_number: p.part_number,
      etag: p.etag,
      size: p.size ?? expectedPartSize(p.part_number, part_size, total_parts, file_size),
    }))
    /* A part the server reports as "uploaded" can still be a truncated leftover from an earlier
       interrupted attempt (e.g. an abort that landed mid-transfer) — it has a real ETag but fewer
       bytes than that part number should have. A non-last part's full size is unambiguous (always
       exactly part_size), so any deviation there is definitely a truncated part worth re-uploading.
       The last part's exact size is more arithmetic-sensitive (file_size/part_size rounding can
       legitimately vary slightly), so it's only rejected if clearly impossible — otherwise trusting
       an exact-match check here was too strict and forced a full restart on perfectly valid resumes. */
    .filter((p) =>
      p.part_number < total_parts
        ? p.size === part_size
        : p.size > 0 && p.size <= part_size,
    )

  return {
    kind: "resume",
    uploadSessionId: uploadSessionIdOf(data),
    part_size,
    total_parts,
    uploadedParts,
    uploadedBytes: data.uploaded_bytes,
    nextPartNumber: data.next_part_number,
  }
}

/** Fetch the freshest session status by session_id and split its uploaded_parts into complete (has etag) vs missing. */
export async function resolveResumeInfo(sessionId: string): Promise<ResumeSessionInfo> {
  const status = await api.getUploadSessionStatus(sessionId)
  return toResumeSessionInfo(status.data)
}

/** Look up an existing session by file fingerprint (name/size/mime/parent/project) — no session_id needed. */
export async function resolveResumeInfoByFingerprint(fingerprint: InitUploadSessionRequest): Promise<ResumeSessionInfo> {
  const status = await api.resumeUploadSession({
    ...fingerprint,
    part_size: computePartSize(fingerprint.file_size),
  })
  return toResumeSessionInfo(status.data)
}
